<template>
  <div class="users-mgmt">
    <div class="users-head">
      <p class="help-text">
        Accounts that can sign in to loggob. Admins can manage users and org settings; everyone can
        view every surface and save their own dashboards, explorations, and filters.
      </p>
      <LgButton variant="primary" size="sm" @click="openAdd">
        <template #icon><UserPlus :size="16" aria-hidden="true" /></template>
        Add user
      </LgButton>
    </div>

    <p v-if="error" class="form-msg form-error" role="alert">{{ error }}</p>

    <div v-if="loading" class="users-loading">Loading users…</div>
    <p v-else-if="!users.length" class="help-text">No users yet.</p>

    <ul v-else class="users-list">
      <li v-for="u in users" :key="u.username" class="user-row">
        <div class="user-id">
          <span class="user-name" :title="u.username">{{ u.username }}</span>
          <span v-if="u.username === currentUser" class="user-you">you</span>
          <span v-if="u.isAdmin" class="user-badge">admin</span>
        </div>

        <div class="user-actions">
          <LgToggle
            :model-value="u.isAdmin"
            :label="`Admin: ${u.username}`"
            :disabled="busy === u.username"
            @update:model-value="(v) => toggleAdmin(u, v)"
          >
            Admin
          </LgToggle>

          <LgButton
            size="sm"
            variant="ghost"
            :disabled="busy === u.username"
            @click="openReset(u.username)"
          >
            <template #icon><KeyRound :size="15" aria-hidden="true" /></template>
            Reset password
          </LgButton>

          <template v-if="confirmDelete === u.username">
            <span class="confirm-label">Delete?</span>
            <LgButton
              size="sm"
              variant="danger"
              :disabled="busy === u.username"
              class="confirm-yes"
              @click="doDelete(u.username)"
            >
              Delete
            </LgButton>
            <LgButton size="sm" variant="ghost" @click="confirmDelete = ''">Cancel</LgButton>
          </template>
          <LgButton
            v-else
            size="sm"
            variant="danger"
            :disabled="busy === u.username"
            class="delete-btn"
            @click="confirmDelete = u.username"
          >
            <template #icon><Trash2 :size="15" aria-hidden="true" /></template>
            Delete
          </LgButton>
        </div>
      </li>
    </ul>

    <!-- Add-user modal -->
    <LgModal :open="addOpen" title="Add user" @close="addOpen = false">
      <form id="add-user-form" class="modal-form" novalidate @submit.prevent="doAdd">
        <label class="field">
          <span class="field-label">Username</span>
          <LgInput
            v-model="addUsername"
            placeholder="e.g. verra"
            autocomplete="off"
            aria-label="New username"
          />
        </label>
        <label class="field">
          <span class="field-label">Password</span>
          <LgInput
            v-model="addPassword"
            type="password"
            placeholder="min 8 characters"
            autocomplete="new-password"
            aria-label="New user password"
          />
        </label>
        <LgToggle v-model="addAdmin" label="Make this user an admin">Admin</LgToggle>
        <p v-if="addError" class="form-msg form-error" role="alert">{{ addError }}</p>
      </form>
      <template #footer>
        <LgButton variant="ghost" size="sm" @click="addOpen = false">Cancel</LgButton>
        <LgButton
          variant="primary"
          size="sm"
          type="submit"
          form="add-user-form"
          :disabled="addBusy"
        >
          {{ addBusy ? 'Adding…' : 'Add user' }}
        </LgButton>
      </template>
    </LgModal>

    <!-- Reset-password modal -->
    <LgModal
      :open="resetOpen"
      :title="`Reset password — ${resetUsername}`"
      @close="resetOpen = false"
    >
      <form id="reset-pw-form" class="modal-form" novalidate @submit.prevent="doReset">
        <p class="help-text">
          Sets a new password for <strong>{{ resetUsername }}</strong> immediately — no current
          password needed. They'll be signed out of other sessions.
        </p>
        <label class="field">
          <span class="field-label">New password</span>
          <LgInput
            v-model="resetPassword"
            type="password"
            placeholder="min 8 characters"
            autocomplete="new-password"
            aria-label="New password"
          />
        </label>
        <p v-if="resetError" class="form-msg form-error" role="alert">{{ resetError }}</p>
      </form>
      <template #footer>
        <LgButton variant="ghost" size="sm" @click="resetOpen = false">Cancel</LgButton>
        <LgButton
          variant="primary"
          size="sm"
          type="submit"
          form="reset-pw-form"
          :disabled="resetBusy"
        >
          {{ resetBusy ? 'Saving…' : 'Set password' }}
        </LgButton>
      </template>
    </LgModal>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { KeyRound, Trash2, UserPlus } from 'lucide-vue-next';
import { api, ApiError, type UserRow } from '../api.js';
import LgButton from '../ui/LgButton.vue';
import LgInput from '../ui/LgInput.vue';
import LgToggle from '../ui/LgToggle.vue';
import LgModal from '../ui/LgModal.vue';

defineProps<{
  /** The signed-in admin, so the row can mark "you". */
  currentUser: string;
}>();

const MIN_PASSWORD_LEN = 8;

const users = ref<UserRow[]>([]);
const loading = ref(true);
const error = ref('');
/** Username currently mid-mutation (disables its row controls). */
const busy = ref('');
const confirmDelete = ref('');

function friendlyError(e: unknown, fallback: string): string {
  if (e instanceof ApiError) {
    // The store's transactional guards return 409 with a clear message
    // ("cannot remove the last admin", etc.) — surface it verbatim.
    if (e.status === 409) return e.message;
    if (e.status === 403) return 'You do not have permission to do that.';
    return e.message || fallback;
  }
  return e instanceof Error ? e.message : fallback;
}

async function load() {
  loading.value = true;
  error.value = '';
  try {
    users.value = await api.users.list();
  } catch (e) {
    error.value = friendlyError(e, 'Failed to load users.');
  } finally {
    loading.value = false;
  }
}

async function toggleAdmin(u: UserRow, next: boolean) {
  error.value = '';
  busy.value = u.username;
  try {
    await api.users.setAdmin(u.username, next);
    u.isAdmin = next; // optimistic; reload keeps ordering/timestamps honest
    await load();
  } catch (e) {
    error.value = friendlyError(e, 'Failed to change admin status.');
  } finally {
    busy.value = '';
  }
}

async function doDelete(username: string) {
  error.value = '';
  busy.value = username;
  try {
    await api.users.delete(username);
    confirmDelete.value = '';
    await load();
  } catch (e) {
    error.value = friendlyError(e, 'Failed to delete user.');
  } finally {
    busy.value = '';
  }
}

// --- Add user ------------------------------------------------------------
const addOpen = ref(false);
const addUsername = ref('');
const addPassword = ref('');
const addAdmin = ref(false);
const addBusy = ref(false);
const addError = ref('');

function openAdd() {
  addUsername.value = '';
  addPassword.value = '';
  addAdmin.value = false;
  addError.value = '';
  addOpen.value = true;
}

async function doAdd() {
  addError.value = '';
  const username = addUsername.value.trim();
  if (!username) {
    addError.value = 'Enter a username.';
    return;
  }
  if (addPassword.value.length < MIN_PASSWORD_LEN) {
    addError.value = `Password must be at least ${String(MIN_PASSWORD_LEN)} characters.`;
    return;
  }
  addBusy.value = true;
  try {
    await api.users.create(username, addPassword.value, addAdmin.value);
    addOpen.value = false;
    await load();
  } catch (e) {
    addError.value = friendlyError(e, 'Failed to add user.');
  } finally {
    addBusy.value = false;
  }
}

// --- Reset password ------------------------------------------------------
const resetOpen = ref(false);
const resetUsername = ref('');
const resetPassword = ref('');
const resetBusy = ref(false);
const resetError = ref('');

function openReset(username: string) {
  resetUsername.value = username;
  resetPassword.value = '';
  resetError.value = '';
  resetOpen.value = true;
}

async function doReset() {
  resetError.value = '';
  if (resetPassword.value.length < MIN_PASSWORD_LEN) {
    resetError.value = `Password must be at least ${String(MIN_PASSWORD_LEN)} characters.`;
    return;
  }
  resetBusy.value = true;
  try {
    await api.users.setPassword(resetUsername.value, resetPassword.value);
    resetOpen.value = false;
  } catch (e) {
    resetError.value = friendlyError(e, 'Failed to reset password.');
  } finally {
    resetBusy.value = false;
  }
}

onMounted(load);
</script>

<style scoped>
.users-mgmt {
  display: flex;
  flex-direction: column;
  gap: var(--lg-space-4);
}
.users-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--lg-space-3);
  flex-wrap: wrap;
}
.help-text {
  margin: 0;
  font-size: var(--lg-fs-sm);
  color: var(--lg-text-dim);
}
.users-loading {
  font-size: var(--lg-fs-sm);
  color: var(--lg-text-dim);
}
.form-msg {
  margin: 0;
  font-size: var(--lg-fs-sm);
}
.form-error {
  color: var(--lg-danger);
}
.users-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: var(--lg-space-3);
}
.user-row {
  display: flex;
  flex-direction: column;
  gap: var(--lg-space-2);
  padding: var(--lg-space-3);
  border: 1px solid var(--lg-border);
  border-radius: var(--lg-radius-md);
  background: var(--lg-bg-surface);
}
.user-id {
  display: flex;
  align-items: center;
  gap: var(--lg-space-2);
  min-width: 0;
}
.user-name {
  font-family: var(--lg-font-mono);
  font-size: var(--lg-fs-md);
  color: var(--lg-text);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.user-you {
  font-size: var(--lg-fs-2xs);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--lg-text-faint);
}
.user-badge {
  font-size: var(--lg-fs-2xs);
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--lg-accent);
  border: 1px solid var(--lg-accent);
  border-radius: var(--lg-radius-sm);
  padding: 0 var(--lg-space-1);
}
.user-actions {
  display: flex;
  align-items: center;
  gap: var(--lg-space-2);
  flex-wrap: wrap;
}
.confirm-label {
  font-size: var(--lg-fs-sm);
  color: var(--lg-danger);
}
.modal-form {
  display: flex;
  flex-direction: column;
  gap: var(--lg-space-4);
}
.field {
  display: flex;
  flex-direction: column;
  gap: var(--lg-space-1);
}
.field-label {
  font-size: var(--lg-fs-xs);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--lg-text-faint);
}
</style>
